import { domain } from 'modules/data';

import { SchedulerFormData } from './Scheduler.types';

/**
 * Generates a Quartz CRON expression based on the scheduler form data
 * Format: seconds minutes hours day-of-month month day-of-week year
 * More details: https://www.quartz-scheduler.net/documentation/quartz-3.x/tutorial/crontriggers.html
 */
export const generateQuartzCronExpression = (values: Partial<SchedulerFormData>): string => {
    // Default values
    let seconds = '0';
    let minutes = '0';
    let hours = '0';
    let dayOfMonth = '*';
    let month = '*';
    let dayOfWeek = '?';
    let year = '*';

    // Parse time (Date object)
    if (values.time && values.time instanceof Date) {
        hours = values.time.getHours().toString();
        minutes = values.time.getMinutes().toString();
    }

    // Handle different scheduler types
    switch (values.dayOption) {
        case domain.ScheduleDayType.EveryDay:
            dayOfMonth = '*';
            dayOfWeek = '?';
            break;

        case domain.ScheduleDayType.EveryDaysOfMonth:
            if (values.nthDayOfMonth && values.startingDayOfMonth) {
                // Every nth day of month, using increment syntax
                const startingDay = parseInt(values.startingDayOfMonth);
                const incrementDay = parseInt(values.nthDayOfMonth);

                dayOfMonth = `${startingDay}/${incrementDay}`;
                dayOfWeek = '?';
            }

            break;

        case domain.ScheduleDayType.SpecificDayOfWeek:
            if (values.weekday && Array.isArray(values.weekday) && values.weekday.length > 0) {
                // Specific days of week
                dayOfWeek = values.weekday
                    .filter(Boolean)
                    .map((day) => day.toUpperCase().substring(0, 3))
                    .join(',');
                dayOfMonth = '?';
            }

            break;

        case domain.ScheduleDayType.SpecificDayOfMonth:
            if (
                values.specificDayOfMonth &&
                Array.isArray(values.specificDayOfMonth) &&
                values.specificDayOfMonth.length > 0
            ) {
                // Specific days of month
                dayOfMonth = values.specificDayOfMonth.filter(Boolean).join(',');
                dayOfWeek = '?';
            }

            break;

        case domain.ScheduleDayType.LastDayOfMonth:
            // Use 'L' character for last day of month
            dayOfMonth = 'L';
            dayOfWeek = '?';
            break;

        case domain.ScheduleDayType.LastWeekdayOfMonth:
            // Use 'LW' character for last weekday of month
            dayOfMonth = 'LW';
            dayOfWeek = '?';
            break;

        case domain.ScheduleDayType.LastDayOfWeekOfMonth:
            if (values.lastDayOfWeekOfMonth) {
                // Format: '?/#' - The last occurrence of a given day of the week in a month
                dayOfMonth = '?';

                // The format is DOW#L where DOW is the day of week (1-7 or SUN-SAT)
                // Convert day name to uppercase 3-letter abbreviation and add L
                const dayOfWeekValue = values.lastDayOfWeekOfMonth.toUpperCase().substring(0, 3);

                dayOfWeek = `${dayOfWeekValue}L`;
            }

            break;

        case domain.ScheduleDayType.LastNDaysBeforeMonthEnd:
            if (values.lastNDaysBeforeMonthEnd) {
                // L-n format: the nth day from the end of the month
                const days = parseInt(values.lastNDaysBeforeMonthEnd);

                if (!isNaN(days)) {
                    dayOfMonth = `L-${days}`;
                    dayOfWeek = '?';
                }
            }

            break;

        case domain.ScheduleDayType.NearestWeekdayToDay:
            if (values.nearestWeekdayToDay) {
                // W format: the nearest weekday to the specified day
                const day = parseInt(values.nearestWeekdayToDay);

                if (!isNaN(day) && day >= 1 && day <= 31) {
                    dayOfMonth = `${day}W`;
                    dayOfWeek = '?';
                }
            }

            break;

        case domain.ScheduleDayType.DayAndWeekdayOfMonth:
            if (values.nthDayOfWeekMonth && values.dayOfWeekOfMonth) {
                // Format: '?/#' - The nth occurrence of a given day of the week in a month
                dayOfMonth = '?';

                const dayOrder = parseInt(values.nthDayOfWeekMonth);

                if (!isNaN(dayOrder) && dayOrder >= 1 && dayOrder <= 5) {
                    const dayOfWeekValue = values.dayOfWeekOfMonth.toUpperCase().substring(0, 3);

                    dayOfWeek = `${dayOfWeekValue}#${dayOrder}`;
                }
            }

            break;
    }

    switch (values.monthOption) {
        case domain.ScheduleMonthType.EveryMonth:
            month = '*';
            break;

        case domain.ScheduleMonthType.EveryMonthsStartingIn:
            if (values.monthPeriod && values.startMonth) {
                // Every nth month starting from specified month
                const startMonthIndex = parseInt(values.startMonth);

                if (!isNaN(startMonthIndex)) {
                    month = `${startMonthIndex}/${values.monthPeriod}`;
                }
            }

            break;

        case domain.ScheduleMonthType.SpecificMonth:
            if (values.specificMonth && Array.isArray(values.specificMonth) && values.specificMonth.length > 0) {
                // Specific months
                month = values.specificMonth.filter(Boolean).join(',');
            }

            break;

        case domain.ScheduleMonthType.EveryMonthBetween:
            if (values.monthBetweenStart && values.monthBetweenEnd) {
                // Range of months
                month = `${values.monthBetweenStart}-${values.monthBetweenEnd}`;
            }

            break;
    }

    switch (values.yearOption) {
        case domain.ScheduleYearType.EveryYear:
            year = '*';
            break;

        case domain.ScheduleYearType.EveryYearsStartingIn: {
            if (values.yearPeriod && values.startYear) {
                const startYear = parseInt(values.startYear);

                if (!isNaN(startYear)) {
                    year = `${startYear}/${values.yearPeriod}`;
                }
            }

            break;
        }

        case domain.ScheduleYearType.SpecificYear: {
            /* Handle specific years:
             * 1. Using specificYearStart and specificYearEnd for a range with possible exclusions
             * 2. Falling back to the specificYear array if needed
             *
             * This makes logic more complex but alternative would be
             * an agressive validation on form which leads to worse UX
             */

            // First try to handle the year range with exclusions approach
            let hasValidRange = false;
            let rangeExpression = '';

            if (values.specificYearStart && values.specificYearEnd) {
                const startYear = parseInt(values.specificYearStart);
                const endYear = parseInt(values.specificYearEnd);

                if (!isNaN(startYear) && !isNaN(endYear) && startYear <= endYear) {
                    hasValidRange = true;

                    // Check if we have exclusions to process
                    const hasExclusions = values.specificYearExcluded && values.specificYearExcluded.length !== 0;

                    if (!hasExclusions) {
                        rangeExpression = `${startYear}-${endYear}`;
                    } else if (values.specificYearExcluded) {
                        // Process exclusions
                        const excludedYears = new Set<number>();

                        for (const yearStr of values.specificYearExcluded) {
                            const yearNum = parseInt(yearStr);

                            if (!isNaN(yearNum) && yearNum >= startYear && yearNum <= endYear) {
                                excludedYears.add(yearNum);
                            }
                        }

                        if (excludedYears.size === 0) {
                            // No valid exclusions, use simple range
                            rangeExpression = `${startYear}-${endYear}`;
                        } else {
                            // Build list of years excluding the excluded ones
                            const allYears = [];

                            for (let y = startYear; y <= endYear; y++) {
                                if (!excludedYears.has(y)) {
                                    allYears.push(y);
                                }
                            }

                            rangeExpression = allYears.join(',');
                        }
                    }
                }
            }

            if (hasValidRange) {
                year = rangeExpression;
            } else if (values.specificYear && Array.isArray(values.specificYear) && values.specificYear.length > 0) {
                // Fallback to specificYear if the range approach didn't work
                year = values.specificYear.filter(Boolean).join(',');
            }

            break;
        }

        case domain.ScheduleYearType.EveryYearBetween: {
            if (values.yearBetweenStart && values.yearBetweenEnd) {
                const startYear = parseInt(values.yearBetweenStart);
                const endYear = parseInt(values.yearBetweenEnd);

                if (!isNaN(startYear) && !isNaN(endYear) && startYear <= endYear) {
                    year = `${startYear}-${endYear}`;
                }
            }

            break;
        }
    }

    // Construct the Quartz CRON expression
    return [seconds, minutes, hours, dayOfMonth, month, dayOfWeek, year].join(' ');
};
